/* import __COLOCATED_TEMPLATE__ from './posting-summary.hbs'; */
import { computed } from '@ember/object';
import { readOnly, equal, bool } from '@ember/object/computed';
import { inject as service } from '@ember/service';
import { task } from 'ember-concurrency';
import Component from '@ember/component';
import { htmlSafe } from '@ember/template';
import { ASSIGNMENT } from 'sh-www/models/assignment';
import ICONS from 'sh-www/constants/assets/icons';
import { showModal } from "sh-www/utils/modal-controls";
import { handleErrorResponseWithModal } from 'sh-www/utils/handles-errors';
import { EMBER_ROUTE_NAMES } from 'sh-www/constants/routes';
import {
  POSTING_CREATE_PRIVACY_LEVEL_TO_TITLES_MAP,
  POSTING_CREATE__QUERY_PARAMS,
} from 'sh-www/constants/routes/posting-create';

export default Component.extend({
  ICONS,

  /**
   * HTML Component properties
   */
  tagName: 'ul',
  classNames: ['SH-c-posting-summary'],

  /**
   * Register services
   */
  assignmentCommandService: service('commands/assignment-commands'),
  applicationStateManager: service('state-managers/application'),
  companyCommandService: service('commands/company-commands'),
  userCommandService: service('commands/user-commands'),
  session: service(),
  moment: service(),
  router: service(),
  media: service(),

  currentUser: readOnly('session.currentUser'),

  /**
   * Get the text describing the assignment creator.
   *
   * @type {String}
   */
  assignmentCreatorText: computed(
    'assignment.buyer.{id,fullName}',
    'currentUser.id',
    function() {
      if (this.currentUser.id === this.assignment.buyer.id) {
        return 'You';
      }

      return this.assignment.buyer.fullName;
    }
  ),

  /**
   * The date timestamp for the relevant date which will be a function of the status on which date to show.
   *
   * @type {String}
   */
  assignmentDateTimestamp: computed(
  'assignment.{status,publishedAt,createdAt,updatedAt}',
  function() {
    const momentService = this.moment;

    switch (this.assignment.status) {
      case ASSIGNMENT.STATUS.CLOSED:
      case ASSIGNMENT.STATUS.LIVE:
        return momentService.moment(this.assignment.publishedAt).format('MM/DD/YYYY');
      case ASSIGNMENT.STATUS.DRAFT:
        return momentService.moment(this.assignment.createdAt).format('MM/DD/YYYY');
      case ASSIGNMENT.STATUS.IN_REVIEW:
        return momentService.moment(this.assignment.updatedAt).format('MM/DD/YYYY');
    }
  }),

  /**
   * Get the class that will go on the element for the assignment status text.
   *
   * @type {String}
   */
  assignmentStatusClass: computed('assignment.status', function() {
    switch (this.assignment.status) {
      case ASSIGNMENT.STATUS.LIVE:
        return 'lsg-green';
      case ASSIGNMENT.STATUS.DRAFT:
        return 'lsg-black';
      case ASSIGNMENT.STATUS.CLOSED:
        return 'lsg-medium-gray';
      case ASSIGNMENT.STATUS.IN_REVIEW:
        return 'lsg-dark-gray';
    }
  }),

  /**
   * Get the text for the assignment status.
   *
   * @type {String}
   */
  assignmentStatusText: computed('assignment.status', function() {
    switch (this.assignment.status) {
      case ASSIGNMENT.STATUS.LIVE:
        return 'Live';
      case ASSIGNMENT.STATUS.DRAFT:
        return 'Draft';
      case ASSIGNMENT.STATUS.CLOSED:
        return 'Closed';
      case ASSIGNMENT.STATUS.IN_REVIEW:
        return 'In Review';
    }
  }),

  assignmentViews: computed('assignment.assignmentStatistics.viewerCount', function() {
    return this.assignment.assignmentStatistics?.viewerCount ?? 0;
  }),

  _assignmentIsClosed: equal('assignment.status', ASSIGNMENT.STATUS.CLOSED),

  /**
   * @type {Boolean}    Only allow unassigned postings to be quick assigned
   */
  _canAssignToTeam: computed(
    'assignment.companyTeam',
    'assignment.buyer.company.canUseCompanyTeams',
    function() {
      return this.assignment.buyer.company.canUseCompanyTeams
        && !this.assignment.companyTeam;
  }),

  /**
   * @type {Boolean} Whether this assignment can be duplicated.
   */
  _canBeDuplicated: readOnly('currentUser.isBuyer'),

  /**
   * @type {Boolean}    True when we can show the cost text.
   */
  _canShowCostText: computed('assignment.{compensationMax,compensationMin}', function() {
    return this.assignment.compensationMax && this.assignment.compensationMin;
  }),

  /**
   * @type {Boolean}    True when we can show the duration text.
   */
   _canShowDurationText: bool('assignment.estimatedDays'),

  /**
   * The cost text of the assignment.
   *
   * @type {String}
   */
  _costText: computed('assignment.{rateType,compensationMin,compensationMax}', function() {
    const rateType = this.assignment.rateType === ASSIGNMENT.RATE_TYPE.DAY ? 'day rate' : 'flat rate';
    return `$${this.assignment.compensationMin} - $${this.assignment.compensationMax} ${rateType}`;
  }),

  descriptionSanitized: computed('assignment.descriptionLinkified', function() {
    return htmlSafe(this.assignment.descriptionLinkified);
  }),

  examplesSanitized: computed('assignment.examplesLinkified', function() {
    return htmlSafe(this.assignment.examplesLinkified);
  }),

  _diyAssignmentInvitesIndicies: computed('assignmentInvites.length', function() {
    return this.assignmentInvites.length - 1;
  }),

  _estimatedDaysText: computed('assignment.estimatedDays', function() {
    return `${this.assignment.estimatedDays} ${this.assignment.estimatedDays === 1 ? 'day' : 'days'}`;
  }),

  _expensesText: computed('assignment.isAllowsExpenses', function() {
    return this.assignment.isAllowsExpenses ? 'Expenses Accepted' : 'No Expenses';
  }),

  /**
   * @type {Number}
   */
  _followersCount: computed('assignment.followers.[]', function() {
    return this.assignment.followers.length;
  }),

  /**
   * The privacy text of the assignment.
   *
   * @type {String}
   */
  _privacyText: computed('assignment.privacyLevel', function() {
    const { privacyLevel } = this.assignment;
    const privacyTitle = POSTING_CREATE_PRIVACY_LEVEL_TO_TITLES_MAP[privacyLevel];

    // have default for legacy
    return privacyTitle ?? POSTING_CREATE_PRIVACY_LEVEL_TO_TITLES_MAP[ASSIGNMENT.PRIVACY_LEVEL.PUBLIC];
  }),

  _selectToggleButtonIcon: computed('assignment.select', function() {
    return this.assignment.select ? 'remove_circle_outline' : 'add_circle_outline';
  }),

  _selectToggleButtonText: computed('assignment.select', function() {
    return this.assignment.select ? 'Remove Select' : 'Make Select';
  }),

  /**
   * The array of services related to this assignment.
   *
   * @type {Ember.MutableArray}
   */
  _services: computed('assignment.services.[]', function() {
    return this.assignment.services;
  }),

  /**
   * Loads the current user's company teams
   *
   * @param {User} currentUser
   * @return {Ember.RSVP.Promise}
   */
  loadUserCompanyTeams: task(function *(currentUser) {
    try {
      const model = currentUser.isCompanyAdmin
        ? yield this.companyCommandService.loadCompany(currentUser.company.id, ['company-teams'])
        : yield this.userCommandService.getUser(currentUser.id, ['company-teams']);

      return model.companyTeams;
    } catch (e) {
      handleErrorResponseWithModal(this, e, 'Failed to load company teams');
    }
  }).drop(),

  /**
   * Update the team an assignment belongs to
   *
   * @param {Assignment} assignment
   * @param {number} companyTeamId id of CompanyTeam
   * @return {Ember.RSVP.Promise}
   */
  saveAssignmentCompanyTeam: task(function *(assignment, companyTeamId) {
    try {
      yield this.assignmentCommandService.setCompanyTeam(assignment.id, companyTeamId);
      yield this.assignmentCommandService.loadAssignment(assignment.id, ['company-team']);

      this.applicationStateManager.addToast('Posting\'s team updated');
    } catch(e) {
      this.applicationStateManager.addToast('Cannot update this posting\'s team');
    }
  }).drop(),

  /**
   * Loads the members of the company.
   *
   * @param {String}
   * @param {Assignment}
   *
   * @return {Ember.RSVP.Promise}
   */
  loadPotentialFollowersTask: task( function *(companyId, subject) {
    const company = yield this.companyCommandService.loadCompany(
      companyId,
      [
        'approved-employees',
      ]
    );

    const employees = company?.approvedEmployees ?? [];
    const potentialFollowers = employees.filter(({ id }) => {
      return this.currentUser.id !== id && // current user not allowed in list
      subject.buyer.id !== id; // the buyer can NOT be removed
    });

    return potentialFollowers;
  }).drop(),

  /**
   * Loads the followers of the assignment.
   *
   * @param {String}
   *
   * @return {Ember.RSVP.Promise}
   */
  loadCurrentFollowersTask: task( function *(subjectId) {
    const assignment = yield this.assignmentCommandService.loadAssignment(
      subjectId,
      [
        'followers',
      ]
    );
    return assignment.followers;
  }).drop(),

  /**
   * Saves the new follower array members.
   *
   * @param {Ember.MutableArray}
   * @param {Ember.MutableArray}
   * @param {String}
   */
  saveFollowersTask: task(function *(newUsersSelected, newUsersDeselected, subjectId) {
    const pending = [];

    if (newUsersSelected.length > 0) {
      pending.push(this.assignmentCommandService.addFollowers(
        subjectId, newUsersSelected, ['followers']));
    }
    if (newUsersDeselected.length > 0) {
      pending.push(this.assignmentCommandService.removeFollowers(
        subjectId, newUsersDeselected));
    }

    try {
      yield Promise.all(pending);
      this.applicationStateManager.addToast('Followers updated');
    } catch (error) {
      handleErrorResponseWithModal(
        this,
        error,
        'There was an error updating the followers. Please refresh and try again.'
      );
    }
  }).enqueue(),

  /**
   * @inheritDoc
   */
  actions: {

    /**
     * Called when the add followers button is clicked on.
     */
    addFollowersClicked: function() {
      const modalModel = {
        title: 'Followers of this posting',
        subTitle: 'Select who should be notified of any changes or messages on this posting and related projects',
        loadPotentialFollowersTask: this.loadPotentialFollowersTask,
        loadCurrentFollowersTask: this.loadCurrentFollowersTask,
        saveFollowersTask: this.saveFollowersTask,
        companyId: this.currentUser.company.id,
        subject: this.assignment
      };

      showModal(this, 'add-followers-modal', modalModel);
    },

    /**
     * Called when the assign team button is clicked on.
     */
    assignTeamClicked: function() {
      const modalModel = {
        assignment: this.assignment,
        title: 'Assign to Team',
        subTitle: `Select a team for "${this.assignment.title}" to be assigned`,
        showDropdownChevron: true,
        disableFiltering: true,
        disableDropdownFiltering: 'Select a Team',
        dropdownOptionsTask: this.loadUserCompanyTeams,
        confirmAction: this.saveAssignmentCompanyTeam,
      };

      showModal(this, 'assign-to-team', modalModel);
    },

    editClicked: function() {
      if (
        this.assignment.select
        && !this.currentUser.isAdmin
        && !!this.assignment.publishedAt
      ) {
        const modalModel = {
          titleText: 'Cannot edit Posting',
          bodyText: 'You cannot edit a Select posting after it has been published.<br /><br />If you need to make changes, please contact your account manager.',
          actionButtonText: 'OK',
        }
        showModal(this, 'alert', modalModel);
        return;
      }

      this.router.transitionTo(
        EMBER_ROUTE_NAMES.POSTING__EDIT,
        this.assignment.idWithSlug,
      );
    },

    /**
     * Called when the user clicks on the 'duplicate' assignment button.
     */
    onDuplicateAssignmentClicked() {
      const assignmentId = this.assignment.id;

      this.applicationStateManager.setNextRoute(EMBER_ROUTE_NAMES.POSTING__CREATE, {
        queryParams: {
          [POSTING_CREATE__QUERY_PARAMS.CLONE_ID]: assignmentId,
        },
      });
    },

    /**
     * Called when an admin clicks on the 'create hunt' button.
     */
    onCreateHuntClicked() {
      this.router.transitionTo(EMBER_ROUTE_NAMES.ADMIN__BLASTER, this.assignment.id);
    },

    onToggleSelectClicked() {
      const title = this.assignment.select ? 'Remove Select Status?' : 'Make Project Select?';
      const body =  this.assignment.select ?
                    `Are you sure you want to remove Select status from project "${this.assignment.title}"?` :
                    `Are you sure you want to make "${this.assignment.title}" a Select project?`;

      showModal(this, 'confirmation', {
        isShowCancelButton: true,
        titleText: title,
        bodyText: body,
        confirmAction: () => {
          this.assignmentCommandService.setSelectStatus(this.assignment.id, !this.assignment.select).then(() => {
            this.assignmentCommandService.loadAssignment(this.assignment.id);
          }).catch((error) => {
            handleErrorResponseWithModal(this, error);
          });
        },
      });
    },
  }
});
